import { QRCodeSVG } from "qrcode.react";
import { X, ShoppingCart, CheckCircle2 } from "lucide-react";

/**
 * CustomerView — full-screen read-only overlay that faces the customer at the
 * banca counter. Shows the current cart (before sale) or the last ticket (after
 * sale) with a huge QR for the customer to scan and keep on their phone.
 */
export default function CustomerView({ open, onClose, cart, cartTotal, businessName, lastTicket }) {
  if (!open) return null;

  const showingLastTicket = !!lastTicket;
  const items = showingLastTicket ? (lastTicket.items || []) : cart;
  const total = showingLastTicket ? (lastTicket.total_amount || 0) : cartTotal;
  const qrValue = showingLastTicket
    ? `${window.location.origin}/t/${encodeURIComponent(lastTicket.ticket_number)}${lastTicket.share_token ? `?v=${encodeURIComponent(lastTicket.share_token)}` : ""}`
    : "";

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 text-white flex flex-col" data-testid="customer-view">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between bg-slate-900/80 backdrop-blur">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">Vista para el Cliente</div>
          <div className="font-black text-2xl tracking-tight" style={{ fontFamily: "Manrope" }}>{businessName || "BANCA POS"}</div>
        </div>
        <button
          onClick={onClose}
          className="p-3 bg-slate-800 hover:bg-slate-700 rounded-sm"
          data-testid="customer-view-close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row items-stretch overflow-hidden">
        {/* LEFT: Items */}
        <div className="flex-1 p-6 lg:p-10 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3">
              <div className="w-24 h-24 rounded-sm bg-slate-800 flex items-center justify-center">
                <ShoppingCart className="w-12 h-12 text-slate-500" />
              </div>
              <div className="text-2xl font-black text-slate-300" style={{ fontFamily: "Manrope" }}>Sin jugadas</div>
              <div className="text-slate-500">El cajero está preparando tu ticket</div>
            </div>
          ) : (
            <>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
                {showingLastTicket ? "Tu Ticket" : "Jugadas en el Ticket"}
              </div>
              <ul className="space-y-3" data-testid="customer-view-items">
                {items.map((it, i) => (
                  <li key={it.id || i} className="flex items-start justify-between gap-4 bg-slate-900 border border-slate-800 rounded-sm p-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {it.game_name || ""} · {it.pt_name || it.play_type_name || ""}
                      </div>
                      <div className="font-mono font-black text-4xl tracking-widest mt-1">{it.display}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold text-2xl">RD${(it.amount || 0).toFixed(2)}</div>
                      {it.potential_prize > 0 && (
                        <div className="text-xs font-mono text-emerald-400 mt-0.5">
                          Premio: RD${(it.potential_prize || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-4 border-t border-slate-800 flex items-baseline justify-between">
                <div className="text-lg font-bold text-slate-400">TOTAL</div>
                <div className="font-mono font-black text-5xl" data-testid="customer-view-total">
                  RD${total.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT: QR panel */}
        {showingLastTicket && (
          <div className="lg:w-[420px] bg-white text-slate-950 p-8 flex flex-col items-center justify-center" data-testid="customer-view-qr">
            <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm mb-4">
              <CheckCircle2 className="w-4 h-4" /> Ticket emitido
            </div>
            <div className="bg-white p-3 border-4 border-slate-950 rounded-sm">
              <QRCodeSVG value={qrValue} size={240} level="M" includeMargin={false} />
            </div>
            <div className="mt-4 text-center">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Número de Ticket</div>
              <div className="font-mono font-black text-lg tracking-wide mt-1">{lastTicket.ticket_number}</div>
            </div>
            <div className="mt-4 text-xs text-slate-500 text-center max-w-[280px]">
              Escanea el QR con tu celular para guardar una copia digital de tu ticket
            </div>
          </div>
        )}
      </div>

      {!showingLastTicket && (
        <div className="bg-amber-500 text-slate-950 px-6 py-3 font-bold text-center animate-pulse">
          Confirma con el cajero antes de pagar. El total mostrado es lo que vas a pagar.
        </div>
      )}
    </div>
  );
}
